import React from 'react';
import styled from 'styled-components';

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: var(--border-radius);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHead = styled.thead`
  background-color: var(--gray-100);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeader = styled.th`
  padding: 0.875rem 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--gray-700);
  border-bottom: 1px solid var(--gray-200);
  white-space: nowrap;
  
  &:first-child {
    border-top-left-radius: var(--border-radius);
  }
  
  &:last-child {
    border-top-right-radius: var(--border-radius);
  }
`;

const TableRow = styled.tr`
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--gray-50);
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
  color: var(--gray-800);
  
  &:first-child {
    padding-left: 1.25rem;
  }
  
  &:last-child {
    padding-right: 1.25rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: var(--gray-500);
  background-color: var(--gray-50);
  border-radius: var(--border-radius);
  margin: 1rem 0;
`;

function DataTable({ columns, data, keyField = '', emptyMessage, expandedRowRender, onRowExpand }) {
  if (!data || data.length === 0) {
    return <EmptyState>{emptyMessage || 'No data available'}</EmptyState>;
  }

  // Track expanded rows
  const [expandedRows, setExpandedRows] = React.useState({});

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
    
    if (onRowExpand) {
      onRowExpand(!expandedRows[rowKey], data.find((row, idx) => 
        (keyField && row[keyField] ? row[keyField] : idx) === rowKey
      ));
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <tr>
            {columns.map((column, index) => (
              <TableHeader key={index}>{column.header || column.Header}</TableHeader>
            ))}
          </tr>
        </TableHead>
        <tbody>
          {data.map((row, rowIndex) => {
            const rowKey = keyField && row[keyField] ? row[keyField] : rowIndex;
            
            return (
              <React.Fragment key={rowKey}>
                <TableRow 
                  onClick={() => expandedRowRender && toggleRowExpanded(rowKey)}
                  style={{ cursor: expandedRowRender ? 'pointer' : 'default' }}
                >
                  {columns.map((column, colIndex) => {
                    const value = typeof column.accessor === 'function' 
                      ? column.accessor(row) 
                      : row[column.accessor];
                    
                    return (
                      <TableCell key={`${rowKey}-${colIndex}`}>
                        {column.render ? column.render(row, rowIndex) : (
                          column.Cell ? column.Cell({ value, row: { original: row }, index: rowIndex }) : (
                            typeof value === 'object' ? 
                              JSON.stringify(value) : 
                              value
                          )
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {expandedRowRender && expandedRows[rowKey] && (
                  <TableRow>
                    <TableCell colSpan={columns.length} style={{ padding: 0 }}>
                      {expandedRowRender(row)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </TableContainer>
  );
}

export default DataTable;